import {
  DISPLAY_COLOR_HEX,
  DISPLAY_POSITION_LINE_COLUMN,
  DISPLAY_POSITION_XY,
  getTftSpiUi,
} from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';
import { beginnerDisplayState, type TftConfig } from './beginner-display-state.js';

const TFT_NAME = 'skyroverTft';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function pinCode(pin: string): string {
  const trimmed = pin.trim();
  if (/^A\d+$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return { r: 255, g: 255, b: 255 };
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function tftColorExpr(colorId: string): string {
  const hex = DISPLAY_COLOR_HEX[colorId] ?? '#FFFFFF';
  const { r, g, b } = hexToRgb(hex);
  return `${TFT_NAME}.color565(${r}, ${g}, ${b})`;
}

function defaultTftConfig(): TftConfig {
  const ui = getTftSpiUi();
  if (ui.mode === 'skyrover-fixed') {
    return {
      mosi: ui.mosi,
      miso: ui.miso,
      sck: ui.sck,
      cs: ui.cs,
      dc: ui.dc,
      reset: ui.reset,
      softwareSpi: true,
    };
  }
  if (ui.mode === 'hardware-spi') {
    return {
      mosi: ui.mosi,
      miso: ui.miso,
      sck: ui.sck,
      cs: '10',
      dc: '9',
      reset: '8',
      softwareSpi: false,
    };
  }
  return {
    mosi: '23',
    miso: '19',
    sck: '18',
    cs: '5',
    dc: '16',
    reset: '17',
    softwareSpi: true,
  };
}

function readTftConfigFromBlock(block: Block): TftConfig {
  const ui = getTftSpiUi();

  if (ui.mode === 'skyrover-fixed') {
    return {
      mosi: ui.mosi,
      miso: ui.miso,
      sck: ui.sck,
      cs: ui.cs,
      dc: ui.dc,
      reset: ui.reset,
      softwareSpi: true,
    };
  }

  if (ui.mode === 'esp-selectable') {
    return {
      mosi: String(block.getFieldValue('MOSI_PIN') ?? '23'),
      miso: String(block.getFieldValue('MISO_PIN') ?? '19'),
      sck: String(block.getFieldValue('SCK_PIN') ?? '18'),
      cs: String(block.getFieldValue('CS_PIN') ?? '5'),
      dc: String(block.getFieldValue('DC_PIN') ?? '16'),
      reset: String(block.getFieldValue('RESET_PIN') ?? '17'),
      softwareSpi: true,
    };
  }

  return {
    mosi: ui.mosi,
    miso: ui.miso,
    sck: ui.sck,
    cs: String(block.getFieldValue('CS_PIN') ?? '10'),
    dc: String(block.getFieldValue('DC_PIN') ?? '9'),
    reset: String(block.getFieldValue('RESET_PIN') ?? '8'),
    softwareSpi: false,
  };
}

function ensureTftSupport(config: TftConfig = beginnerDisplayState.tft ?? defaultTftConfig()): void {
  beginnerDisplayState.tft = config;
  arduinoGenerator.addInclude('#include <SPI.h>');
  arduinoGenerator.addInclude('#include <Adafruit_GFX.h>');
  arduinoGenerator.addInclude('#include <Adafruit_ST7735.h>');

  const cs = pinCode(config.cs);
  const dc = pinCode(config.dc);
  const rst = pinCode(config.reset);

  if (!beginnerDisplayState.tftDeclared) {
    if (config.softwareSpi) {
      const mosi = pinCode(config.mosi);
      const sck = pinCode(config.sck);
      arduinoGenerator.addGlobal(
        `Adafruit_ST7735 ${TFT_NAME} = Adafruit_ST7735(${cs}, ${dc}, ${mosi}, ${sck}, ${rst});`,
      );
    } else {
      arduinoGenerator.addGlobal(
        `Adafruit_ST7735 ${TFT_NAME} = Adafruit_ST7735(${cs}, ${dc}, ${rst});`,
      );
    }
    beginnerDisplayState.tftDeclared = true;
  }

  arduinoGenerator.addSetupOnce('beginner-tft-init', `${TFT_NAME}.initR(INITR_BLACKTAB);`);
  arduinoGenerator.addSetupOnce('beginner-tft-rotation', `${TFT_NAME}.setRotation(0);`);
}

function resolveTftCursor(block: Block): { x: number; y: number } {
  const mode = String(block.getFieldValue('POSITION_MODE') ?? DISPLAY_POSITION_LINE_COLUMN);
  const textSize = Math.max(1, beginnerDisplayState.tftTextSize);

  if (mode === DISPLAY_POSITION_XY) {
    return {
      x: Math.max(0, Math.round(Number(block.getFieldValue('X') ?? 0))),
      y: Math.max(0, Math.round(Number(block.getFieldValue('Y') ?? 0))),
    };
  }

  const line = Math.max(1, Math.round(Number(block.getFieldValue('LINE') ?? 1)));
  const column = Math.max(1, Math.round(Number(block.getFieldValue('COLUMN') ?? 1)));
  return {
    x: (column - 1) * 6 * textSize,
    y: (line - 1) * 8 * textSize,
  };
}

function colorFromBlock(block: Block, field = 'COLOR'): string {
  return tftColorExpr(String(block.getFieldValue(field) ?? 'WHITE'));
}

export function registerBeginnerTftGenerators(): void {
  arduinoGenerator.forBlock['beginner_tft_configure'] = (block: Block) => {
    ensureTftSupport(readTftConfigFromBlock(block));
    return statementPrefix(block, '// TFT configured\n');
  };

  arduinoGenerator.forBlock['beginner_tft_clear'] = (block: Block) => {
    ensureTftSupport();
    return statementPrefix(block, `${TFT_NAME}.fillScreen(ST77XX_BLACK);\n`);
  };

  arduinoGenerator.forBlock['beginner_tft_fill_screen'] = (block: Block) => {
    ensureTftSupport();
    return statementPrefix(block, `${TFT_NAME}.fillScreen(${colorFromBlock(block)});\n`);
  };

  arduinoGenerator.forBlock['beginner_tft_display_text'] = (block: Block) => {
    ensureTftSupport();
    const text = arduinoGenerator.valueToCode(block, 'TEXT', Order.NONE) || '""';
    const { x, y } = resolveTftCursor(block);
    const size = Math.max(1, beginnerDisplayState.tftTextSize);
    return statementPrefix(
      block,
      [
        `${TFT_NAME}.setTextSize(${size});`,
        `${TFT_NAME}.setCursor(${x}, ${y});`,
        `${TFT_NAME}.print(${text});`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_tft_set_cursor'] = (block: Block) => {
    ensureTftSupport();
    const { x, y } = resolveTftCursor(block);
    return statementPrefix(block, `${TFT_NAME}.setCursor(${x}, ${y});\n`);
  };

  arduinoGenerator.forBlock['beginner_tft_set_text_size'] = (block: Block) => {
    ensureTftSupport();
    const size = Math.min(5, Math.max(1, Math.round(Number(block.getFieldValue('SIZE') ?? 1))));
    beginnerDisplayState.tftTextSize = size;
    return statementPrefix(block, `${TFT_NAME}.setTextSize(${size});\n`);
  };

  arduinoGenerator.forBlock['beginner_tft_set_text_color'] = (block: Block) => {
    ensureTftSupport();
    return statementPrefix(block, `${TFT_NAME}.setTextColor(${colorFromBlock(block)});\n`);
  };

  arduinoGenerator.forBlock['beginner_tft_draw_pixel'] = (block: Block) => {
    ensureTftSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawPixel(${x}, ${y}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_draw_line'] = (block: Block) => {
    ensureTftSupport();
    const x1 = Math.round(Number(block.getFieldValue('X1') ?? 0));
    const y1 = Math.round(Number(block.getFieldValue('Y1') ?? 0));
    const x2 = Math.round(Number(block.getFieldValue('X2') ?? 0));
    const y2 = Math.round(Number(block.getFieldValue('Y2') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawLine(${x1}, ${y1}, ${x2}, ${y2}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_draw_rectangle'] = (block: Block) => {
    ensureTftSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawRect(${x}, ${y}, ${w}, ${h}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_fill_rectangle'] = (block: Block) => {
    ensureTftSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.fillRect(${x}, ${y}, ${w}, ${h}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_draw_circle'] = (block: Block) => {
    ensureTftSupport();
    const cx = Math.round(Number(block.getFieldValue('CX') ?? 0));
    const cy = Math.round(Number(block.getFieldValue('CY') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawCircle(${cx}, ${cy}, ${r}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_fill_circle'] = (block: Block) => {
    ensureTftSupport();
    const cx = Math.round(Number(block.getFieldValue('CX') ?? 0));
    const cy = Math.round(Number(block.getFieldValue('CY') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.fillCircle(${cx}, ${cy}, ${r}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_draw_triangle'] = (block: Block) => {
    ensureTftSupport();
    const x1 = Math.round(Number(block.getFieldValue('X1') ?? 0));
    const y1 = Math.round(Number(block.getFieldValue('Y1') ?? 0));
    const x2 = Math.round(Number(block.getFieldValue('X2') ?? 0));
    const y2 = Math.round(Number(block.getFieldValue('Y2') ?? 0));
    const x3 = Math.round(Number(block.getFieldValue('X3') ?? 0));
    const y3 = Math.round(Number(block.getFieldValue('Y3') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawTriangle(${x1}, ${y1}, ${x2}, ${y2}, ${x3}, ${y3}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_fill_triangle'] = (block: Block) => {
    ensureTftSupport();
    const x1 = Math.round(Number(block.getFieldValue('X1') ?? 0));
    const y1 = Math.round(Number(block.getFieldValue('Y1') ?? 0));
    const x2 = Math.round(Number(block.getFieldValue('X2') ?? 0));
    const y2 = Math.round(Number(block.getFieldValue('Y2') ?? 0));
    const x3 = Math.round(Number(block.getFieldValue('X3') ?? 0));
    const y3 = Math.round(Number(block.getFieldValue('Y3') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.fillTriangle(${x1}, ${y1}, ${x2}, ${y2}, ${x3}, ${y3}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_draw_rounded_rectangle'] = (block: Block) => {
    ensureTftSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.drawRoundRect(${x}, ${y}, ${w}, ${h}, ${r}, ${colorFromBlock(block)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_tft_fill_rounded_rectangle'] = (block: Block) => {
    ensureTftSupport();
    const x = Math.round(Number(block.getFieldValue('X') ?? 0));
    const y = Math.round(Number(block.getFieldValue('Y') ?? 0));
    const w = Math.round(Number(block.getFieldValue('WIDTH') ?? 0));
    const h = Math.round(Number(block.getFieldValue('HEIGHT') ?? 0));
    const r = Math.round(Number(block.getFieldValue('RADIUS') ?? 0));
    return statementPrefix(
      block,
      `${TFT_NAME}.fillRoundRect(${x}, ${y}, ${w}, ${h}, ${r}, ${colorFromBlock(block)});\n`,
    );
  };
}

registerBeginnerTftGenerators();
